/**
 * Conjugate into a slot — the tier-3 Motor mode (TASKS T51, SPEC §5).
 *
 * The learner is given an infinitive, a person, and a tense, and a real sentence with the verb taken
 * out of it. He produces the form. Nothing on the card contains it, there is no table to read off,
 * and the endings are not listed — this is retrieval of a form, not recognition of one.
 *
 * Two decisions matter here and both are about *not* being a grammar drill.
 *
 *  - **The slot is inside a sentence, not on its own.** `ser, yo, present` with an empty box is a
 *    conjugation-table exercise; `Yo ___ de Guadalajara.` is a sentence with a hole in it. Tier 3 is
 *    subject to the 25% grammar cap precisely because explicit grammar study is this learner's known
 *    over-investment (BRIEF <conversion_engine>), and a mode that *looks* like a paradigm table
 *    invites exactly that. The hole is cut with the same function the cloze mode uses.
 *  - **The spec is named in English, plainly.** `él / ella / usted` rather than "3rd person
 *    singular": the learner is producing Spanish, not learning grammatical terminology, and a card
 *    that costs him a second to parse is a card he answers slower for no pedagogical reason.
 *
 * Pure. No DOM, no clock, no store.
 */

import type { Person, Tense } from '../lang/conjugation';
import { cutCloze } from './cloze';
import type { ModeInput, Presentation, PromptSegment } from './types';

/**
 * How each person is shown.
 *
 * `él / ella / usted` is one slot with three subjects, and saying so is worth the extra characters:
 * a learner who reads only `él` will be surprised by `usted` later, and `usted` taking the same form
 * is one of the genuinely useful facts about Mexican Spanish. `vosotros` is not here and cannot be —
 * the tables have five persons, not six (D-066).
 */
export const PERSON_LABEL: Readonly<Record<Person, string>> = {
  yo: 'yo',
  tu: 'tú',
  el: 'él / ella / usted',
  nosotros: 'nosotros',
  ustedes: 'ustedes',
};

/** Tense names as the learner meets them. English, because the instruction line is English. */
export const TENSE_LABEL: Readonly<Record<Tense, string>> = {
  present: 'present',
  preterite: 'preterite',
  imperfect: 'imperfect',
  subjunctive: 'present subjunctive',
  conditional: 'conditional',
};

/** The one line naming what the slot wants: `ser — yo, present`. */
export function slotLabel(infinitive: string, tense: Tense, person: Person): string {
  return `${infinitive} — ${PERSON_LABEL[person]}, ${TENSE_LABEL[tense]}`;
}

/**
 * The help this card offers if asked (invariant 2 — asking marks the attempt hinted, T54).
 *
 * The first letter and the length, exactly as the cloze scaffold does, and deliberately not "the
 * stem is `est-`". Handing over the stem of an irregular hands over the irregularity, which is the
 * entire thing the item exists to teach; for a regular verb it hands over everything but the ending.
 */
export function conjugateScaffold(form: string): string | null {
  const letters = [...form.trim()];
  const first = letters[0];
  if (!first) return null;
  return `Starts with "${first}" — ${letters.length === 1 ? '1 letter' : `${letters.length} letters`}.`;
}

/**
 * Build the conjugation card.
 *
 * `expected` is the item's `es` — the surface form — and not something re-derived from the tables
 * here. The two are checked against each other at build time (`tests/unit/content.spec.ts` runs the
 * corpus through `conjugate()`), so a mis-typed form in a JSON file fails CI rather than quietly
 * teaching a wrong form. Re-deriving at runtime would mean the card could disagree with the item the
 * scheduler is tracking, which is worse than either being wrong on its own.
 */
export function conjugatePresentation(input: ModeInput): Presentation {
  const { item, extras } = input;
  const slot = extras.slot;
  const scaffold = conjugateScaffold(item.es);

  if (!slot) {
    // No slot means this is not really a conjugation item — a candidate minted from a conversation
    // log, most likely. It is still due, and invariant 7 says a review is never withheld.
    return {
      mode: 'conjugate',
      instruction: 'Say it in Spanish',
      segments: [{ kind: 'text', text: item.en }],
      gloss: null,
      expected: item.es,
      scaffold,
      input: 'text',
      fieldLabel: 'Your answer',
      announce: `${item.en}. Type it in Spanish.`,
    };
  }

  const instruction = `Conjugate ${slotLabel(slot.infinitive, slot.tense, slot.person)}`;
  /*
   * The slot, handed to the classifier so the feedback can say "wrong person" (T56). Without it,
   * `hablas` for `hablo` is a spelling slip as far as the two strings are concerned, and the learner
   * is sent to look for a typo in a form he conjugated to the wrong person.
   */
  const context = { verb: { infinitive: slot.infinitive, tense: slot.tense, person: slot.person } };
  const sentence = extras.example?.es;
  const cut = sentence ? cutCloze(sentence, item.es) : null;

  if (!cut) {
    // Without a carrier sentence the card is the slot on its own. Honest, and duller — which is why
    // every tier-3 seed item ships with an example.
    return {
      mode: 'conjugate',
      instruction,
      segments: [{ kind: 'blank', length: [...item.es].length }],
      gloss: item.en,
      expected: item.es,
      context,
      scaffold,
      input: 'text',
      fieldLabel: 'The form',
      announce: `${instruction}. ${item.en}.`,
    };
  }

  const segments: PromptSegment[] = [];
  if (cut.before) segments.push({ kind: 'text', text: cut.before });
  segments.push({ kind: 'blank', length: [...item.es].length });
  if (cut.after) segments.push({ kind: 'text', text: cut.after });

  return {
    mode: 'conjugate',
    instruction,
    segments,
    gloss: extras.example?.en ?? item.en,
    expected: item.es,
    context,
    scaffold,
    input: 'text',
    fieldLabel: 'The form',
    announce: `${instruction}. ${cut.before}blank${cut.after}`.replace(/\s+/g, ' ').trim(),
  };
}
